/**
 * Zod-free locale constants. Published as `@unibyte/contracts/locales` so the widget loader and
 * @unibyte/i18n can import them without pulling the zod runtime into browser bundles.
 *
 * Supported UI locales. This list is data, not three hardcoded branches:
 * adding a locale means adding a dictionary in @unibyte/i18n and enabling it per bot.
 */
export const SUPPORTED_LOCALES = ['el', 'en', 'fr'];
export const DEFAULT_LOCALE = 'el';
export function isLocale(value) {
    return typeof value === 'string' && SUPPORTED_LOCALES.includes(value);
}
const ENGLISH_WORDS = [
    'the',
    'and',
    'you',
    'for',
    'with',
    'this',
    'that',
    'what',
    'when',
    'where',
    'who',
    'how',
    'does',
    'are',
    'can',
    'about',
    'from',
    'please',
    'hello',
    'your',
];
const FRENCH_WORDS = [
    'le',
    'la',
    'les',
    'des',
    'une',
    'est',
    'vous',
    'pour',
    'dans',
    'avec',
    'bonjour',
    'merci',
    'quels',
    'quelle',
    'comment',
];
/** Guide §10. Short or mixed text stays ambiguous so the conversation locale is kept. */
export function detectMessageLocale(text) {
    if (/[\u0370-\u03FF\u1F00-\u1FFF]/u.test(text))
        return 'el';
    const tokens = text
        .toLowerCase()
        .split(/[^\p{L}]+/u)
        .filter((token) => token.length > 0);
    if (tokens.join('').length < 12)
        return 'ambiguous';
    const present = new Set(tokens);
    const hits = (words) => words.filter((word) => present.has(word)).length;
    const english = hits(ENGLISH_WORDS);
    const french = hits(FRENCH_WORDS);
    if (french >= 2 && french > english)
        return 'fr';
    if (english >= 2 && english > french)
        return 'en';
    if (tokens.join('').length >= 24 && english === 0 && french === 0)
        return 'unsupported';
    return 'ambiguous';
}
/**
 * 1. Explicit visitor selection until they change it.
 * 2. Auto: reliable language of a substantial message.
 * 3. Short or ambiguous text keeps the conversation locale.
 * 4. Callers pass the site/browser locale as the conversation locale before the first message.
 */
export function resolveMessageLocale(input) {
    const enabled = new Set(input.enabled);
    const fallback = enabled.has(input.conversationLocale)
        ? input.conversationLocale
        : (input.enabled[0] ?? DEFAULT_LOCALE);
    if (input.mode === 'manual') {
        if (input.selectedLocale && enabled.has(input.selectedLocale)) {
            return { locale: input.selectedLocale, unsupported: false };
        }
        return { locale: fallback, unsupported: true };
    }
    const detected = detectMessageLocale(input.text);
    if (detected === 'unsupported')
        return { locale: fallback, unsupported: true };
    if (detected !== 'ambiguous' && enabled.has(detected)) {
        return { locale: detected, unsupported: false };
    }
    return { locale: fallback, unsupported: false };
}
/**
 * Visitor-facing locale selection mode (guide §10).
 * - auto: resolved from message language / conversation / site default
 * - manual: explicit visitor selection, persists until changed
 */
export const LOCALE_MODES = ['auto', 'manual'];
//# sourceMappingURL=locales.js.map